/**
 * Hook that handles user identity sync with analytics.
 * Identifies once per user session and only resets on sign-out.
 * Tracks sign-up (new accounts) and sign-in (returning accounts).
 * Org switches update super properties without resetting the profile.
 */

import type { ClerkOrganization, ClerkUser } from '@saaga/shared/interfaces/auth.interface'
import type { AuthMethod } from '@saaga/shared/interfaces/gtm.interface'
import {
  identifyUser,
  resetAnalytics,
  setAnalyticsUserContext,
  setUserProfileProperties,
  trackEvent,
} from '@saaga/shared/utils/analytics.util'
import { pushGtmLogin, pushGtmSignUp } from '@saaga/shared/utils/gtm.util'
import { useEffect } from 'react'

const SIGN_UP_THRESHOLD_MS = 2 * 60 * 1000

const trackSignUp = (method: AuthMethod): void => {
  trackEvent('user_signed_up', { method })
  pushGtmSignUp(method)
}

const trackSignIn = (method: AuthMethod): void => {
  trackEvent('user_signed_in', { method })
  pushGtmLogin(method)
}

const handleUserChanged = (
  user: ClerkUser,
  previousUserId: string | null,
  organization: ClerkOrganization | null
): void => {
  resetAnalytics()
  identifyUser(user.id)

  setAnalyticsUserContext({
    userId: user.id,
    orgId: organization?.id ?? '',
    orgSlug: organization?.slug ?? '',
    email: user.email,
  })

  const method = (user.authMethod ?? 'email') as AuthMethod
  const isNewAccount = Date.now() - user.createdAt < SIGN_UP_THRESHOLD_MS

  if (!previousUserId && isNewAccount) {
    trackSignUp(method)
    return
  }
  trackSignIn(method)
}

const syncUserProfile = (user: ClerkUser, organization: ClerkOrganization | null): void => {
  setUserProfileProperties({
    $email: user.email,
    $first_name: user.firstName ?? '',
    $last_name: user.lastName ?? '',
    orgId: organization?.id ?? '',
    orgSlug: organization?.slug ?? '',
  })
}

let identifiedUserId: string | null = null

export const useAnalyticsIdentitySync = (
  user: ClerkUser | null,
  organization: ClerkOrganization | null
): void => {
  useEffect(() => {
    if (!user) {
      if (identifiedUserId) {
        resetAnalytics()
        identifiedUserId = null
      }
      return
    }

    if (identifiedUserId !== user.id) {
      handleUserChanged(user, identifiedUserId, organization)
      identifiedUserId = user.id
    }

    syncUserProfile(user, organization)
  }, [user, organization])
}
